import { 
  IPerformanceProfiler, 
  ProfilerConfig, 
  PerformanceMetrics, 
  ProfilerReport 
} from '../types/devtools-interfaces';

/**
 * Профилировщик производительности для CSElement
 * Отслеживает время выполнения операций и использование памяти
 */
export class PerformanceProfiler implements IPerformanceProfiler {
  private _config: ProfilerConfig;
  private _measurements: Map<string, { startTime: number; metadata?: Record<string, any> }>;
  private _history: PerformanceMetrics[];
  private _isEnabled: boolean;

  constructor(config: Partial<ProfilerConfig> = {}) {
    this._config = {
      enabled: true,
      trackMemory: true,
      trackOperations: true,
      sampleRate: 1.0, // 100% sampling by default
      maxHistorySize: 1000,
      ...config
    };

    this._measurements = new Map();
    this._history = [];
    this._isEnabled = this._config.enabled;
  }

  get config(): ProfilerConfig {
    return { ...this._config };
  }

  /**
   * Начать измерение операции
   */
  startMeasurement(operationType: string, metadata?: Record<string, any>): string {
    if (!this._isEnabled || !this._config.trackOperations) {
      return '';
    }

    // Проверяем sample rate
    if (Math.random() > this._config.sampleRate) {
      return '';
    }

    const measurementId = `${operationType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this._measurements.set(measurementId, {
      startTime: performance.now(),
      metadata
    });

    return measurementId;
  }

  /**
   * Завершить измерение операции
   */
  endMeasurement(measurementId: string): PerformanceMetrics {
    const measurement = this._measurements.get(measurementId);
    
    if (!measurement || !this._isEnabled) {
      // Возвращаем пустую метрику если измерение не найдено
      return {
        operationType: 'unknown',
        duration: 0,
        elementCount: 0,
        memoryUsage: 0,
        timestamp: Date.now()
      };
    }

    const endTime = performance.now();
    const duration = endTime - measurement.startTime;
    
    // Извлекаем тип операции из ID
    const operationType = measurementId.split('_')[0];
    
    const metrics: PerformanceMetrics = {
      operationType,
      duration,
      elementCount: this.getElementCount(),
      memoryUsage: this._config.trackMemory ? this.getMemoryUsage() : 0,
      timestamp: Date.now(),
      metadata: measurement.metadata
    };

    // Удаляем измерение из активных
    this._measurements.delete(measurementId);

    // Добавляем в историю
    this.addToHistory(metrics);

    return metrics;
  }

  /**
   * Получить отчет за период
   */
  getReport(timeRange?: { start: number; end: number }): ProfilerReport {
    let operations = this._history;

    // Фильтруем по временному диапазону если указан
    if (timeRange) {
      operations = operations.filter(op => 
        op.timestamp >= timeRange.start && op.timestamp <= timeRange.end
      );
    }

    if (operations.length === 0) {
      return {
        timeRange: timeRange || { start: 0, end: Date.now() },
        operations: [],
        summary: {
          totalOperations: 0,
          averageDuration: 0,
          peakMemoryUsage: 0,
          slowestOperations: []
        }
      };
    }

    // Вычисляем статистику
    const totalOperations = operations.length;
    const totalDuration = operations.reduce((sum, op) => sum + op.duration, 0);
    const averageDuration = totalDuration / totalOperations;
    const peakMemoryUsage = Math.max(...operations.map(op => op.memoryUsage));
    
    // Находим самые медленные операции (топ 10)
    const slowestOperations = [...operations]
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10);

    return {
      timeRange: timeRange || {
        start: Math.min(...operations.map(op => op.timestamp)),
        end: Math.max(...operations.map(op => op.timestamp))
      },
      operations,
      summary: {
        totalOperations,
        averageDuration,
        peakMemoryUsage,
        slowestOperations
      }
    };
  }

  /**
   * Очистить историю метрик
   */
  clearHistory(): void {
    this._history = [];
  }

  /**
   * Включить/выключить профилировщик
   */
  setEnabled(enabled: boolean): void {
    this._isEnabled = enabled;
    this._config.enabled = enabled;
  }

  /**
   * Обновить конфигурацию профилировщика
   */
  updateConfig(config: Partial<ProfilerConfig>): void {
    this._config = { ...this._config, ...config };
    this._isEnabled = this._config.enabled;
  }

  /**
   * Получить статистику по типам операций
   */
  getOperationStats(): Record<string, {
    count: number;
    totalDuration: number;
    averageDuration: number;
    maxDuration: number;
    minDuration: number;
  }> {
    const stats: Record<string, any> = {};

    this._history.forEach(metric => {
      const type = metric.operationType;
      
      if (!stats[type]) {
        stats[type] = {
          count: 0,
          totalDuration: 0,
          maxDuration: 0,
          minDuration: Infinity
        };
      }

      stats[type].count++;
      stats[type].totalDuration += metric.duration;
      stats[type].maxDuration = Math.max(stats[type].maxDuration, metric.duration);
      stats[type].minDuration = Math.min(stats[type].minDuration, metric.duration);
    });

    // Вычисляем средние значения
    Object.keys(stats).forEach(type => {
      stats[type].averageDuration = stats[type].totalDuration / stats[type].count;
      if (stats[type].minDuration === Infinity) {
        stats[type].minDuration = 0;
      }
    });

    return stats;
  }

  /**
   * Получить активные измерения
   */
  getActiveMeasurements(): string[] {
    return Array.from(this._measurements.keys());
  }

  /**
   * Отменить активное измерение
   */
  cancelMeasurement(measurementId: string): boolean {
    return this._measurements.delete(measurementId);
  }

  /**
   * Создать снимок текущего состояния
   */
  createSnapshot(): {
    config: ProfilerConfig;
    activeMeasurements: number;
    historySize: number;
    isEnabled: boolean;
    operationStats: Record<string, any>;
  } {
    return {
      config: this.config,
      activeMeasurements: this._measurements.size,
      historySize: this._history.length,
      isEnabled: this._isEnabled,
      operationStats: this.getOperationStats()
    };
  }

  /**
   * Добавить метрику в историю
   */
  private addToHistory(metrics: PerformanceMetrics): void {
    this._history.push(metrics);

    // Ограничиваем размер истории
    if (this._history.length > this._config.maxHistorySize) {
      this._history.shift();
    }
  }

  /**
   * Получить текущее количество элементов в системе
   */
  private getElementCount(): number {
    try {
      // Импортируем CSElement динамически чтобы избежать циклических зависимостей
      const { CSElement } = require('../core/CSElement');
      return CSElement.totalElementsCount || 0;
    } catch {
      return 0;
    }
  }

  /**
   * Получить текущее использование памяти
   */
  private getMemoryUsage(): number {
    if (typeof performance !== 'undefined' && 'memory' in performance) {
      // В Chrome доступна информация о памяти
      const memory = (performance as any).memory;
      return memory.usedJSHeapSize || 0;
    }

    // Fallback: приблизительная оценка через GC
    if (typeof global !== 'undefined' && global.gc) {
      try {
        global.gc();
        return process.memoryUsage().heapUsed;
      } catch {
        return 0;
      }
    }

    return 0;
  }

  /**
   * Создать профиль производительности для функции
   */
  profileFunction<T extends (...args: any[]) => any>(
    fn: T, 
    operationType: string, 
    metadata?: Record<string, any>
  ): T {
    return ((...args: any[]) => {
      const measurementId = this.startMeasurement(operationType, metadata);
      
      try {
        const result = fn(...args);
        
        // Если результат - промис, обрабатываем асинхронно
        if (result && typeof result.then === 'function') {
          return result.finally(() => {
            this.endMeasurement(measurementId);
          });
        }
        
        // Синхронная функция
        this.endMeasurement(measurementId);
        return result;
      } catch (error) {
        this.endMeasurement(measurementId);
        throw error;
      }
    }) as T;
  }

  /**
   * Создать декоратор для профилирования методов
   */
  static createProfileDecorator(profiler: PerformanceProfiler, operationType?: string) {
    return function(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
      const originalMethod = descriptor.value;
      const opType = operationType || `${target.constructor.name}.${propertyKey}`;

      descriptor.value = function(...args: any[]) {
        const measurementId = profiler.startMeasurement(opType, {
          className: target.constructor.name,
          methodName: propertyKey,
          args: args.length
        });

        try {
          const result = originalMethod.apply(this, args);
          
          if (result && typeof result.then === 'function') {
            return result.finally(() => {
              profiler.endMeasurement(measurementId);
            });
          }
          
          profiler.endMeasurement(measurementId);
          return result;
        } catch (error) {
          profiler.endMeasurement(measurementId);
          throw error;
        }
      };

      return descriptor;
    };
  }
} 